import { clampText, resolveAbsoluteUrl, stripHtml } from "./seoUtils";
import { CONTACT_INFO, DEFAULT_LOGO, SITE_NAME, SITE_URL } from "./siteConfig";

const currencyCode = "INR";

export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_LOGO,
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: CONTACT_INFO.phone[0],
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: ["en", "hi"],
    },
  ],
});

export const createWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/product?query={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const createBreadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items
    .filter((item) => item?.name && item?.url)
    .map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: resolveAbsoluteUrl(item.url),
    })),
});

export const createFaqSchema = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs
    .filter((faq) => faq?.question && faq?.answer)
    .map((faq) => ({
      "@type": "Question",
      name: stripHtml(faq.question),
      acceptedAnswer: {
        "@type": "Answer",
        text: stripHtml(faq.answer),
      },
    })),
});

export const createProductSchema = (product = {}, pageUrl = "/") => {
  const productName = product.productName || product.name || SITE_NAME;
  const images = Array.isArray(product.images)
    ? product.images
        .map((image) => image?.url || image)
        .filter(Boolean)
        .map((image) => resolveAbsoluteUrl(image))
    : [];

  const variants = Array.isArray(product.variants) ? product.variants : [];
  const basePrice =
    product.basePrice || variants[0]?.price || product.price || product.offer?.offerPrice || 0;
  const offerType = product.offer?.offerType;
  const finalPrice =
    offerType === "offerPrice"
      ? product.offer?.offerPrice || basePrice
      : offerType === "percentOff" && product.offer?.percentOff
        ? basePrice - basePrice * (product.offer.percentOff / 100)
        : basePrice;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productName,
    image: images.length > 0 ? images : [resolveAbsoluteUrl("/Logo.png")],
    description: clampText(product.description || product.shortDescription || "", 240),
    sku: product.sku || product._id || product.id,
    brand: {
      "@type": "Brand",
      name: SITE_NAME,
    },
    offers: {
      "@type": "Offer",
      url: resolveAbsoluteUrl(pageUrl),
      priceCurrency: currencyCode,
      price: Number(finalPrice || basePrice || 0),
      availability: product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
    aggregateRating:
      product.averageRating || product.rating
        ? {
            "@type": "AggregateRating",
            ratingValue: Number(product.averageRating || product.rating || 0).toFixed(1),
            reviewCount: Number(product.reviewCount || product.reviews?.length || 0),
          }
        : undefined,
  };
};

export const createArticleSchema = ({ title, description, image, url, datePublished, dateModified }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description: stripHtml(description),
  image: image ? resolveAbsoluteUrl(image) : undefined,
  url: resolveAbsoluteUrl(url),
  author: {
    "@type": "Organization",
    name: SITE_NAME,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: {
      "@type": "ImageObject",
      url: DEFAULT_LOGO,
    },
  },
  datePublished,
  dateModified: dateModified || datePublished,
});

export const createContactPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: `${SITE_NAME} Contact`,
  url: `${SITE_URL}/contact`,
  mainEntity: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    telephone: CONTACT_INFO.phone,
    email: CONTACT_INFO.email,
  },
});

export const createWebPageSchema = ({ name, description, url, hasPart = [] }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  description: stripHtml(description),
  url: resolveAbsoluteUrl(url),
  isPartOf: {
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
  },
  hasPart: hasPart
    .filter(Boolean)
    .map((part) => ({
      "@type": "WebPageElement",
      name: part.name,
      description: stripHtml(part.description || ""),
    })),
});
